import { useMe } from "@/api/services/main/auth";
import { IconBackArrow, IconChevronDown, IconForwardArrow, IconHelpOutline, IconLogo, IconNotification } from "@/assets/icons";
import { Popover } from "@/components/elements/Popover";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { AppRoutes } from "@/routes/config";
import { background, divider, text } from "@/theme/colors";
import { elevation, spacing } from "@/theme/variables";
import { Box, Button, Stack, styled, Tooltip, Typography } from "@mui/material";
import zIndex from "@mui/material/styles/zIndex";
import { FC } from "react";
import { Link, useNavigate } from "react-router-dom";
import { truncateWithEllipsis } from "@/utils/string";
import { NotificationModal } from "@/features/information/components/NotificationModal";
import { useVisible } from "@/hooks/useVisible";
import { useUnreadNotification } from "@/hooks/useUnreadNotification";

type TAppHeaderProps = {
  isOpenDrawer: boolean;
  toggleDrawer: () => void;
};

export const AppHeader: FC<TAppHeaderProps> = ({ isOpenDrawer, toggleDrawer }) => {
  const { isLaptop } = useMediaQuery();
  const navigate = useNavigate();
  const { data: meResult } = useMe();
  const meData = meResult?.data;
  const checkResult = useUnreadNotification();
  const notificationModal = useVisible();

  return (
    <BoxWrapper>
      <Stack direction="row" alignItems="center" gap={1}>
        {isLaptop && (
          <Tooltip title={isOpenDrawer ? "メニューを最小化" : "メニューを最大化"} placement="bottom-start">
            <Button onClick={toggleDrawer}>
              {isOpenDrawer ? <IconBackArrow width={32} height={32} /> : <IconForwardArrow width={32} height={32} />}
            </Button>
          </Tooltip>
        )}
        <StyledLink to={AppRoutes.home}>
          <IconLogo />
        </StyledLink>
      </Stack>
      <AppMenu mt={1}>
        <Stack gap={3} flexDirection="row" alignItems="center">
          <Typography component="a" href="https://support.iro-hana.com/irohana-study-34c5d48302ae8018a9a4ff786964094d" target="_blank" rel="noopener noreferrer">
            <IconHelpOutline cursor="pointer" fontSize={20} color={text.white}/>
          </Typography>
          <Typography onClick={notificationModal.open}>
            <IconNotification cursor="pointer" fontSize={24} hasDot={!!checkResult?.data} />
          </Typography>
        </Stack>
        <NameTypography mb={1}>{meData ? `${truncateWithEllipsis(meData?.rso?.name ?? meData?.company?.companyName ?? "", 20)}　|　${truncateWithEllipsis(meData?.fullName ?? "", 20)}` : "---- | ----"}</NameTypography>
        <Popover
          sx={{
            marginTop: "8px",
            borderRadius: 0,
            ".MuiPopover-paper": {
              borderRadius: "0 0 8px 8px",
              boxShadow: "0px 4px 4px 0px #00000040",
            },
          }}
          anchor={<IconChevronDown cursor="pointer" fontSize="24" color={text.white} />}
        >
          <LogoutOption
            onClick={() => {
              navigate(AppRoutes.logout);
            }}
          >
            ログアウト
          </LogoutOption>
        </Popover>
      </AppMenu>
      <NotificationModal modal={notificationModal} />
    </BoxWrapper>
  );
};

const BoxWrapper = styled(Box)`
  background-color: ${background.main};
  height: 56px;
  padding: 0px ${spacing.md};
  border-bottom: 1px solid ${divider.middle};
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: ${elevation.lv1};
  position: sticky;
  top: 0;
  z-index: ${zIndex.appBar};
`;

const AppMenu = styled(Box)`
  display: flex;
  align-items: center;
`;

const NameTypography = styled(Typography)`
  color: ${text.white};
  margin-right: 13px;
  margin-left: 24px;
  font-size: 14px;
  line-height: 21px;
  font-weight: 400;
`;

const StyledLink = styled(Link)`
  display: inline-flex;
  &.centered {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const LogoutOption = styled(Box)`
  font-size: 14px;
  padding: 4px 28px;
  cursor: pointer;
  :hover {
    background: ${divider.low};
    color: ${text.primary};
  }
  :focus {
    background: ${divider.middle};
    color: ${text.primary};
  }
`;
